import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Plus, Edit, Trash2, Loader2, Search, FileText } from 'lucide-react'
import { apiGet } from '../lib/api'

interface BlogData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: Array<{
    id: string
    titulo: string
    slug: string
    imagen: string
    publicado: boolean
    fechaPublicacion: string
    categoriaId: string
  }>
}

export function Blog() {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const qc = useQueryClient()

  const { data, isLoading, isFetching } = useQuery<BlogData>({
    queryKey: ['blog', page, searchTerm],
    queryFn: () =>
      apiGet(`/api/admin/blog?page=${page}${searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ''}`),
    placeholderData: (prev) => prev,
  })

  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/admin/blog/${id}`, { method: 'DELETE' })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blog'] }),
  })

  function onDelete(id: string, titulo: string) {
    if (!confirm(`¿Eliminar el post "${titulo}"?`)) return
    del.mutate(id)
  }

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchTerm(q.trim())
  }

  return (
    <>
      <Topbar
        title="Blog"
        subtitle={data ? `${data.total.toLocaleString('es-MX')} posts` : 'Cargando…'}
        actions={
          <Link
            to="/blog/nuevo"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nuevo post
          </Link>
        }
      />
      <div className="p-6">
        <form onSubmit={onSearch} className="mb-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por título o slug…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
            </div>
          )}

          {data && data.data.length === 0 && (
            <div className="grid place-items-center py-16 text-sm text-ink-500">
              No hay posts.
            </div>
          )}

          {data && data.data.length > 0 && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Post</th>
                      <th className="px-4 py-3 font-medium">Slug</th>
                      <th className="px-4 py-3 font-medium">Fecha</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                      <th className="w-24 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((b) => (
                      <tr
                        key={b.id}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {b.imagen ? (
                              <img
                                src={b.imagen}
                                alt=""
                                className="size-12 shrink-0 rounded-lg object-cover"
                                onError={(e) => {
                                  ;(e.target as HTMLImageElement).style.display = 'none'
                                }}
                              />
                            ) : (
                              <div className="grid size-12 shrink-0 place-items-center rounded-lg bg-ink-100 text-ink-500">
                                <FileText className="size-5" />
                              </div>
                            )}
                            <div className="line-clamp-2 max-w-md font-medium text-ink-900">
                              {b.titulo}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-500">{b.slug}</td>
                        <td className="px-4 py-3 text-ink-700">
                          {b.fechaPublicacion || '—'}
                        </td>
                        <td className="px-4 py-3">
                          {b.publicado ? (
                            <Badge variant="success">Publicado</Badge>
                          ) : (
                            <Badge variant="warning">Borrador</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Link
                              to="/blog/$id"
                              params={{ id: b.id }}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                            >
                              <Edit className="size-4" />
                            </Link>
                            <button
                              onClick={() => onDelete(b.id, b.titulo)}
                              disabled={del.isPending}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong>{' '}
                  ({data.total.toLocaleString('es-MX')} posts)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
